import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useAuth, API } from "../../context/AuthContext";
import PendingApprovals from "./PendingApprovals";
import RevealPayout from "./RevealPayout";
import ExpenseSettlementGroup, { groupExpenses } from "./ExpenseSettlementGroup";
import { FileText, Download, Check, X, DollarSign, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

export default function TasksSection({ onCountChange }) {
  const [subTab, setSubTab] = useState("registrations");
  const [pendingCount, setPendingCount] = useState(0);
  const [expenseCount, setExpenseCount] = useState(0);
  const [settlementCount, setSettlementCount] = useState(0);

  useEffect(() => { fetchCounts(); }, []);
  const { authAxios } = useAuth();

  const fetchCounts = async () => {
    try {
      const [expRes, settRes] = await Promise.all([
        authAxios.get("/expenses/pending"),
        authAxios.get("/settlements/pending"),
      ]);
      setExpenseCount(expRes.data.length);
      const d = settRes.data;
      setSettlementCount((d.open_expenses || []).length + (d.merchant_payouts || []).filter(p => p.remaining > 0).length);
    } catch (err) { console.error("Request failed:", err?.message || err); }
  };

  const handlePendingCount = (c) => { setPendingCount(c); onCountChange?.(c + expenseCount); };

  const subTabs = [
    { id: "registrations", label: "Memberships", badge: pendingCount },
    { id: "expenses", label: "Expenses", badge: expenseCount },
    { id: "settlements", label: "Settlements", badge: settlementCount },
  ];

  return (
    <div data-testid="tasks-section">
      <div className="flex gap-2 mb-4 overflow-x-auto">
        {subTabs.map(st => (
          <button key={st.id} onClick={() => setSubTab(st.id)} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap relative ${subTab === st.id ? "bg-[#1e3a5f] text-white" : "bg-white text-slate-600 border"}`} data-testid={`task-${st.id}`}>
            {st.label}
            {st.badge > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">{st.badge}</span>}
          </button>
        ))}
      </div>

      {subTab === "registrations" && <PendingApprovals onCountChange={handlePendingCount} />}
      {subTab === "expenses" && <ExpenseApprovals onRefresh={fetchCounts} />}
      {subTab === "settlements" && <Settlements onRefresh={fetchCounts} />}
    </div>
  );
}

function ExpenseTarget({ expense, openEvents, onChange }) {
  const current = expense.event_id || "general";
  const known = current === "general" || openEvents.some(ev => ev.id === current);
  return (
    <div className="mb-2 flex items-center gap-2">
      <span className="text-[11px] font-semibold text-slate-500 whitespace-nowrap">Belongs to</span>
      <select value={current} onChange={(ev) => ev.target.value !== current && onChange(ev.target.value)}
        className="flex-1 min-w-0 px-2 py-1.5 rounded-lg border border-slate-200 bg-white text-xs" data-testid={`expense-target-${expense.id}`}>
        <option value="general">General expense (no event)</option>
        {!known && <option value={current} disabled>{expense.event_name} (closed)</option>}
        {openEvents.map(ev => <option key={ev.id} value={ev.id}>Event: {ev.name}</option>)}
      </select>
    </div>
  );
}

function ExpenseApprovals({ onRefresh }) {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [rejectDialog, setRejectDialog] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [expCategories, setExpCategories] = useState([]);
  const [openEvents, setOpenEvents] = useState([]);
  const { authAxios } = useAuth();

  useEffect(() => {
    fetchExpenses(); fetchCategories();
    authAxios.get("/expenses/open-events").then(r => setOpenEvents(r.data)).catch(() => {});
  }, []);
  const moveExpense = async (id, value) => {
    const body = value === "general" ? { kind: "general" } : { kind: "event", event_id: value };
    try {
      const r = await authAxios.put(`/expenses/${id}/kind`, body);
      const { expense_kind, event_id, event_name } = r.data;
      setExpenses(list => list.map(x => x.id === id ? { ...x, expense_kind, event_id, event_name } : x));
      toast.success(value === "general" ? "Moved to General" : "Moved to event");
    }
    catch (err) { toast.error(err.response?.data?.detail || "Failed to move expense"); }
  };
  const fetchCategories = async () => {
    try { const res = await authAxios.get("/categories"); setExpCategories(res.data.filter(c => c.type === "expense")); } catch (err) { console.error("Request failed:", err?.message || err); }
  };
  const fetchExpenses = async () => {
    try { const res = await authAxios.get("/expenses/pending"); setExpenses(res.data); }
    catch { toast.error("Failed to load expenses"); }
    finally { setLoading(false); }
  };

  const handleApprove = async (id) => {
    try { await authAxios.post(`/expenses/${id}/approve`); toast.success("Expense approved"); fetchExpenses(); onRefresh?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleApproveBatch = async (items) => {
    const missing = items.filter(x => !x.category);
    if (missing.length > 0) return toast.error(`${missing.length} line(s) still need a category before approving.`);
    try {
      const r = await authAxios.post("/expenses/approve-bulk", { expense_ids: items.map(x => x.id) });
      if (r.data.failed.length) toast.warning(`${r.data.count} approved, ${r.data.failed.length} failed: ${r.data.failed[0].reason}`);
      else toast.success(`${r.data.count} lines (€${r.data.total.toFixed(2)}) approved — one email sent`);
    } catch (e) { toast.error(e.response?.data?.detail || "Batch approve failed"); }
    fetchExpenses(); onRefresh?.();
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) return toast.error("Reason is required");
    try { await authAxios.post(`/expenses/${rejectDialog}/reject`, { reason: rejectReason }); toast.success("Expense rejected"); setRejectDialog(null); setRejectReason(""); fetchExpenses(); onRefresh?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const viewAttachment = async (id) => {
    try {
      const r = await fetch(`${API}/expenses/${id}/attachment`, {
        credentials: "include",
      });
      if (!r.ok) {
        if (r.status === 404) return toast.info("No attachment");
        throw new Error(await r.text());
      }
      const blob = await r.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch { toast.error("Failed to load attachment"); }
  };

  if (loading) return <div className="text-center py-8 text-slate-400">Loading...</div>;
  if (expenses.length === 0) return <div className="bg-white rounded-2xl p-8 text-center shadow-sm"><p className="text-slate-500">No pending expenses</p></div>;

  // Group by person + payout account (same rule as Settlements); singletons render flat.
  const batches = [];
  const idxByBatch = new Map();
  for (const e of expenses) {
    const bid = e.payout_group || `_solo_${e.id}`;
    if (idxByBatch.has(bid)) {
      batches[idxByBatch.get(bid)].items.push(e);
    } else {
      idxByBatch.set(bid, batches.length);
      batches.push({ batch_id: bid, items: [e] });
    }
  }

  const renderLine = (e, inBatch = false) => (
    <div key={e.id} className={`bg-white ${inBatch ? "rounded-xl border border-slate-100" : "rounded-2xl shadow-sm"} p-4`} data-testid={inBatch ? `batch-line-${e.id}` : `expense-${e.id}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          {!inBatch && <p className="font-bold text-[#1e3a5f]">{e.submitter_name}</p>}
          {!inBatch && <p className="text-slate-500 text-xs">{e.submitter_email} | {e.event_name}</p>}
          {inBatch && <p className="text-slate-500 text-xs">{e.event_name} · {new Date(e.created_at).toLocaleDateString()}</p>}
        </div>
        <div className="text-right">
          <p className="font-bold text-lg text-[#1e3a5f]">&euro;{e.amount.toFixed(2)}</p>
          <button onClick={() => setExpanded(expanded === e.id ? null : e.id)} className="text-xs text-slate-400">{expanded === e.id ? <ChevronUp className="w-4 h-4 inline" /> : <ChevronDown className="w-4 h-4 inline" />} Details</button>
        </div>
      </div>
      <p className="text-sm text-slate-600 mb-2">{e.description}</p>
      <ExpenseTarget expense={e} openEvents={openEvents} onChange={(v) => moveExpense(e.id, v)} />
      <div className="mb-2">
        <select value={e.category || ""} onChange={async (ev) => {
          try {
            const category = ev.target.value;
            await authAxios.put(`/expenses/${e.id}/category`, { category });
            setExpenses(list => list.map(x => x.id === e.id ? { ...x, category } : x));
            toast.success("Category set");
          } catch { toast.error("Failed"); }
        }} className={`w-full px-2 py-1.5 rounded-lg border text-xs ${e.category ? "border-green-300 bg-green-50" : "border-amber-300 bg-amber-50"}`}>
          <option value="">Assign Category *</option>
          {expCategories.map(c => <option key={c.id} value={c.name}>{c.name}{c.is_capex ? " (CAPEX)" : ""}</option>)}
        </select>
      </div>
      {expanded === e.id && (
        <div className="bg-slate-50 rounded-xl p-3 mb-2 text-xs space-y-1">
          {e.payout_method === "wallet" ? (
            <p><strong>Payout:</strong> Wallet ({e.payout_wallet_id || e.payout_wallet_email || "—"})</p>
          ) : <RevealPayout expense={e} />}
          {e.invoice_number && <p><strong>Invoice #:</strong> {e.invoice_number}</p>}
          {e.invoice_date && <p><strong>Invoice Date:</strong> {e.invoice_date}</p>}
          <p><strong>Submitted:</strong> {new Date(e.created_at).toLocaleString()}</p>
          {e.attachment_name && (
            <button onClick={() => viewAttachment(e.id)} className="flex items-center gap-1 text-blue-600 hover:underline mt-1">
              <FileText className="w-3 h-3" /> {e.attachment_name}
            </button>
          )}
        </div>
      )}
      <div className="flex gap-2">
        <button
          onClick={() => e.category ? handleApprove(e.id) : toast.error("Assign a category before approving this line")}
          disabled={!e.category}
          className="flex-1 py-2 rounded-xl bg-green-500 hover:bg-green-600 text-white font-bold text-sm flex items-center justify-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`approve-expense-${e.id}`}
          title={!e.category ? "Assign a category before approving" : "Approve this line"}
        ><Check className="w-4 h-4" /> Approve</button>
        <button onClick={() => { setRejectDialog(e.id); setRejectReason(""); }} className="flex-1 py-2 rounded-xl bg-red-500 text-white font-bold text-sm flex items-center justify-center gap-1"><X className="w-4 h-4" /> Reject</button>
      </div>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="expense-approvals">
      {batches.map(b => {
        if (b.items.length === 1) return renderLine(b.items[0]);
        const head = b.items[0];
        const total = b.items.reduce((s, x) => s + (x.amount || 0), 0);
        const payoutLabel = head.payout_method === "wallet" ? `Wallet (${head.payout_wallet_id || head.payout_wallet_email || "—"})`
                          : head.payout_method === "paypal" ? `PayPal (${head.payout_paypal_id || "—"})`
                          : head.iban ? `IBAN ${head.iban}` : "IBAN —";
        const events = [...new Set(b.items.map(x => x.event_name))].join(", ");
        const missingCats = b.items.filter(x => !x.category).length;
        return (
          <div key={b.batch_id} className="rounded-2xl border-2 border-purple-200 bg-purple-50/40 overflow-hidden" data-testid={`expense-batch-${b.batch_id}`}>
            <div className="px-4 py-3 bg-purple-100/60 flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <p className="font-bold text-[#1e3a5f] text-sm truncate">{head.submitter_name} <span className="text-xs text-slate-500 font-normal">({b.items.length} lines)</span></p>
                <p className="text-purple-700 text-xs truncate">{head.submitter_email} · {payoutLabel}</p>
                <p className="text-slate-500 text-[11px] truncate">{events}</p>
                {missingCats > 0 && (
                  <p className="text-[10px] text-amber-700 mt-0.5">{missingCats} line(s) still need a category before you can approve the whole batch.</p>
                )}
              </div>
              <div className="text-right">
                <p className="font-extrabold text-[#1e3a5f]">&euro;{total.toFixed(2)}</p>
                <p className="text-[10px] text-slate-500">Total</p>
              </div>
              <button
                onClick={() => handleApproveBatch(b.items)}
                disabled={missingCats > 0}
                className="px-3 py-2 rounded-full bg-green-500 hover:bg-green-600 text-white text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid={`batch-approve-${b.batch_id}`}
                title={missingCats > 0 ? "Assign a category to every line first" : `Approve all ${b.items.length} lines`}
              ><Check className="w-3.5 h-3.5 inline mr-1" /> Approve all</button>
            </div>
            <div className="p-3 space-y-2 bg-white border-t border-purple-100">
              {b.items.map(item => renderLine(item, true))}
            </div>
          </div>
        );
      })}

      <AlertDialog open={!!rejectDialog} onOpenChange={(open) => { if (!open) setRejectDialog(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject Expense</AlertDialogTitle>
            <AlertDialogDescription>Please provide a reason for rejection (sent to submitter).</AlertDialogDescription>
          </AlertDialogHeader>
          <textarea value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Reason for rejection..." className="w-full p-3 rounded-xl border text-sm" rows={3} />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleReject}>Reject</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Settlements({ onRefresh }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { authAxios } = useAuth();

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => {
    setLoading(true);
    try { const res = await authAxios.get("/settlements/pending"); setData(res.data); }
    catch { toast.error("Failed to load settlements"); }
    finally { setLoading(false); }
  };

  const handleMarkPaid = async (items) => {
    try {
      if (items.length === 1) {
        await authAxios.post(`/expenses/${items[0].id}/mark-paid`);
        toast.success("Marked as paid");
      } else {
        const r = await authAxios.post("/expenses/mark-paid-bulk", { expense_ids: items.map(e => e.id) });
        if (r.data.failed.length) toast.warning(`${r.data.paid} paid, ${r.data.failed.length} failed: ${r.data.failed[0].reason}`);
        else toast.success(`${r.data.paid} expenses (€${r.data.total.toFixed(2)}) marked as paid — one email sent`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    fetchData(); onRefresh?.();
  };

  const handleMerchantPaid = async (item) => {
    try {
      await authAxios.post("/merchant-settlement/mark-paid", { event_id: item.event_id, merchant_id: item.merchant_id, amount: item.remaining });
      toast.success(`Merchant payout marked as paid`);
      fetchData(); onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  if (loading) return <div className="text-center py-8 text-slate-400">Loading...</div>;
  if (!data) return null;

  const groups = groupExpenses(data.open_expenses);
  const merchants = (data.merchant_payouts || []).filter(p => p.remaining > 0);

  return (
    <div data-testid="settlements">
      <h3 className="font-bold text-[#1e3a5f] mb-1">Open Settlements ({data.open_expenses.length + merchants.length})</h3>
      {groups.length + merchants.length === 0 && (
        <div className="bg-white rounded-2xl p-6 text-center shadow-sm mb-6"><p className="text-slate-500 text-sm">No open settlements</p></div>
      )}
      {groups.length > 0 && (
        <>
          <p className="text-xs text-slate-500 mb-2">Expenses grouped by person and payout account</p>
          <div className="space-y-2 mb-6" data-testid="settle-expense-groups">
            {groups.map(g => <ExpenseSettlementGroup key={g.key} group={g} onPay={handleMarkPaid} />)}
          </div>
        </>
      )}
      {merchants.length > 0 && (
        <div className="space-y-2 mb-6">
          {merchants.map(item => (
            <div key={item.id || `${item.event_id}-${item.merchant_id}`} className="bg-white rounded-2xl shadow-sm p-3 flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-700">MERCHANT</span>
                  <p className="font-medium text-sm truncate">{item.merchant_name}</p>
                </div>
                <p className="text-xs text-slate-500">{item.event_name} ({item.event_dates})</p>
                <p className="text-xs text-slate-400">Sales: {"\u20ac"}{item.total_sales.toFixed(2)} | Margin: {item.margin}% | Due: {"\u20ac"}{item.payout_due.toFixed(2)}{item.already_paid > 0 ? ` | Paid: \u20ac${item.already_paid.toFixed(2)}` : ""}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="font-bold text-[#1e3a5f]">{"\u20ac"}{item.remaining.toFixed(2)}</span>
                <button onClick={() => handleMerchantPaid(item)} className="px-2.5 py-1.5 rounded-lg bg-green-500 text-white text-xs font-bold" data-testid={`pay-merchant-${item.id}`}>Mark paid</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Unsettled events */}
      <h3 className="font-bold text-[#1e3a5f] mb-3">Unsettled Events ({data.unsettled_events.length})</h3>
      {data.unsettled_events.length === 0 ? (
        <div className="bg-white rounded-2xl p-6 text-center shadow-sm"><p className="text-slate-500 text-sm">All events settled</p></div>
      ) : (
        <div className="space-y-2">
          {data.unsettled_events.map(ev => (
            <div key={ev.id} className="bg-white rounded-2xl p-3 shadow-sm flex items-center justify-between">
              <div>
                <p className="font-medium text-sm">{ev.name}</p>
                <p className="text-xs text-slate-500">{ev.start_date} - {ev.end_date}</p>
              </div>
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-600">Open</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
